import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_COMPANY_PRODUCTS, ADD_UPDATE_COMPANY_PRODUCT, DELETE_COMPANY_PRODUCT, Get_Expert_Video, GET_PRODUCT_COUNTS } from '../../../../services/userAPI';
import { Trash2, Calendar, FileText, Video, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { message } from 'antd';
import { useLocation } from 'react-router-dom';
import editIcon from '../../../../Images/editIcon.png';

const ProductUpdatesAdmin = () => {
  const location = useLocation();
  const ROLE_ID = location.state?.roleId ? parseInt(location.state.roleId) : null;
  const ACCOUNT_ID = location.state?.accountId ? parseInt(location.state.accountId) : null;
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    fullDescription: '',
    shortDescription: '',
    productName: '',
    contentType: 'text',
    content: '',
    youtubeCode: '',
    videoTitle: '',
    thumbnailCode: '',
    videoDuration: '',
    language: '',
    publishDate: '',
    expiryDate: '',
    description: '',
    videoDescription: '',
  });

  const [errors, setErrors] = useState({});

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
    nextCursor: null,
    prevCursor: null,
  });

  const { data: updatesData, loading, refetch } = useQuery(GET_COMPANY_PRODUCTS, {
    variables: {
      accountId: ACCOUNT_ID,
      roleId: ROLE_ID,
      pagingCriteria: {
        limit: 5,
        cursor: pagination.cursor || null,
        sortByField: 'created_at',
        sortOrder: 'desc'
      },
      productStatus: filterStatus === 'all' ? null : filterStatus.toUpperCase()
    },
    fetchPolicy: 'network-only',
    skip: !ACCOUNT_ID || !ROLE_ID
  });

  // ✅ UPDATE pagination with API meta
  useEffect(() => {
    if (companyProductsResponse?.pageMeta) {
      const meta = companyProductsResponse.pageMeta;
      setPagination(prev => ({
        ...prev,
        total: meta.totalCount,
        nextCursor: meta.nextPage,
        prevCursor: meta.previousPage
      }));
    }
  }, [updatesData]);

  const { data: languagesData } = useQuery(Get_Expert_Video);

  const [addUpdate] = useMutation(ADD_UPDATE_COMPANY_PRODUCT, {
    onCompleted: () => { refetch(); resetForm(); refetchCounts(); },
    onError: (err) => message.error('Save failed: ' + err.message)
  });

  const [updateProduct] = useMutation(ADD_UPDATE_COMPANY_PRODUCT, {
    onCompleted: () => { refetch(); resetForm(); refetchCounts(); }
  });

  const [deleteProduct, { loading: deleteLoading }] = useMutation(DELETE_COMPANY_PRODUCT, {
    onCompleted: (data) => {
      if (data?.deleteProducts?.success) {
        message.success(data.deleteProducts.message || 'Product deleted successfully!');
        refetch(); // Refresh list
        setShowDeleteModal(false);
        setDeleteCandidate(null);
        refetchCounts(); // Refresh counts
      } else {
        message.error(data?.deleteProducts?.message || 'Failed to delete product');
      }
    },
    onError: (error) => {
      console.error('Delete error:', error);
      message.error('Delete failed: ' + error.message);
    }
  });

  // Add this query right after your GET_COMPANY_PRODUCTS query
  const { data: countsData, loading: countsLoading, refetch: refetchCounts } = useQuery(GET_PRODUCT_COUNTS, {
    variables: {
      accountId: ACCOUNT_ID,
      jobrole: ROLE_ID
    },
    skip: !ACCOUNT_ID,
    fetchPolicy: 'network-only'
  });

  // SAFELY EXTRACT UPDATES — THIS FIXES THE ERROR
  const companyProductsResponse = updatesData?.getCompanyProducts;
  const updates = Array.isArray(companyProductsResponse?.data)
    ? companyProductsResponse.data
    : [];

  // Auto thumbnail
  useEffect(() => {
    if (formData.contentType === 'video' && formData.youtubeCode.trim()) {
      const code = formData.youtubeCode.trim();
      setFormData(prev => ({
        ...prev,
        thumbnailCode: `https://img.youtube.com/vi/${code}/0.jpg`
      }));
    } else {
      setFormData(prev => ({ ...prev, thumbnailCode: '' }));
    }
  }, [formData.youtubeCode, formData.contentType]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.productName.trim()) newErrors.productName = 'Product name is required';
    if (!formData.publishDate) newErrors.publishDate = 'Publish date is required';

    if (formData.contentType === 'text' && !formData.content.trim()) {
      newErrors.content = 'Text content is required';
    }

    if (formData.contentType === 'video') {
      if (!formData.youtubeCode.trim()) newErrors.youtubeCode = 'YouTube code is required';
      if (formData.youtubeCode.length !== 11) newErrors.youtubeCode = 'YouTube code must be exactly 11 characters';
      if (!formData.videoTitle.trim()) newErrors.videoTitle = 'Video title is required';
      if (!formData.language) newErrors.language = 'Language is required';
      if (!/^([0-5]?[0-9]):[0-5][0-9]$/.test(formData.videoDuration)) {
        newErrors.videoDuration = 'Enter valid duration: MM:SS';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleDurationChange = (e) => {
    let value = e.target.value.replace(/\D/g, '').slice(0, 4);
    let formatted = value;
    if (value.length >= 3) formatted = value.slice(0, 2) + ':' + value.slice(2);
    setFormData(prev => ({ ...prev, videoDuration: formatted }));
    if (errors.videoDuration) setErrors(prev => ({ ...prev, videoDuration: '' }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const [inputMins = '0', inputSecs = '0'] = formData.videoDuration.split(':');
    const mins = parseInt(inputMins, 10) || 0;
    const secs = parseInt(inputSecs, 10) || 0;

    // Format as MM:SS string with leading zeros
    const formattedDuration = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

    const baseInput = {
      accountId: ACCOUNT_ID,
      roleId: ROLE_ID,
      productName: formData.productName.trim(),
      title: formData.title.trim(),
      shortDescription: formData.description.trim(),
      publishDate: formData.publishDate,
      expiryDate: formData.expiryDate || null,
      description: formData.content.trim(),
    };

    setIsSubmitting(true);
    try {
      let finalInput;

      if (formData.contentType === 'text') {
        finalInput = {
          ...baseInput,
          content: null
        };
      } else {
        finalInput = {
          ...baseInput,
          content: {
            id: null,
            title: formData.videoTitle.trim(),
            languageId: parseInt(formData.language),
            commitmentNeeded: false,
            description: formData.videoDescription.trim(),
            code: formData.youtubeCode.trim(),
            thumbnailUrl: formData.thumbnailCode,
            lengthMins: formattedDuration,
            category: "PRODUCT",
            contentTags: ["video", "update"]
          }
        };
      }

      editingId
        ? await updateProduct({ variables: { input: { ...finalInput, id: editingId } } })
        : await addUpdate({ variables: { input: finalInput } });

      message.success('Saved successfully!');
      resetForm();
    } catch (err) {
      message.error('Save failed: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (update) => {
    const video = update.productVideo || {};

    // Calculate duration from videoLengthMins + videoLengthSecs
    let durationStr = '';
    if (video.videoLengthMins !== undefined && video.videoLengthSecs !== undefined) {
      const totalSeconds = (video.videoLengthMins || 0) * 60 + (video.videoLengthSecs || 0);
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      durationStr = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    setFormData({
      title: update.title || '',
      description: update.description || video.description || '',
      productName: update.productName || '',
      contentType: video.code ? 'video' : 'text',
      content: !video.code ? update.description || '' : '',
      youtubeCode: video.code || '',
      videoTitle: video.title || '',
      thumbnailCode: video.code ? `https://img.youtube.com/vi/${video.code}/0.jpg` : '',
      videoDuration: durationStr, // Now correctly filled (e.g., "21:21")
      language: video.languageId?.toString() || '',
      publishDate: update.publishDate?.split('T')[0] || '',
      expiryDate: update.expiryDate?.split('T')[0] || '',
      videoDescription: video.description || ''
    });

    setEditingId(update.id);
    setShowForm(true);
    setErrors({});
  };

  const openDeleteModal = (id) => {
    setDeleteCandidate(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;

    try {
      await deleteProduct({
        variables: { productId: deleteCandidate } // Correct variable name
      });
      // onCompleted will handle success
    } catch (err) {
      // onError will handle it
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', productName: '', contentType: 'text',
      content: '', youtubeCode: '', videoTitle: '', thumbnailCode: '',
      videoDuration: '', language: '', publishDate: '', expiryDate: '',
      videoDescription: ''
    });
    setEditingId(null);
    setShowForm(false);
    setErrors({});
  };

  const filteredUpdates = updates;

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-140px)] bg-white rounded-lg p-4 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const handlePageChange = (page) => {
    let newCursor = null;

    if (page > pagination.current) newCursor = pagination.nextCursor;
    else if (page < pagination.current) newCursor = pagination.prevCursor;

    setPagination(prev => ({ ...prev, current: page, cursor: newCursor }));

    refetch({
      accountId: ACCOUNT_ID,
      roleId: ROLE_ID,
      pagingCriteria: {
        limit: pagination.pageSize,
        cursor: newCursor,
        sortByField: 'created_at',
        sortOrder: 'desc'
      },
      productStatus: filterStatus === 'all' ? null : filterStatus.toUpperCase()
    });
  };

  return (
    <>
    <div className="bg-white rounded-lg p-5">
      
      <div className="mx-auto">
        <div className="mb-4">
          <h1 className="text-lg font-bold text-gray-900 mb-1">Product Updates Management</h1>
          {/* <p className="text-gray-600">Manage product updates for the Know Your Role section</p> */}
        </div>

        {/* Status Cards */}
        {/* Status Cards - Now from API */}
        {countsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-[92px] bg-gray-50 border border-gray-200 rounded-xl p-3 animate-pulse">
                <div className="h-4 bg-gray-300 rounded w-20 mb-2"></div>
                <div className="h-7 bg-gray-300 rounded w-12"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="h-[92px] bg-green-50 border border-green-300 rounded-xl p-3">
              <p className="text-green-600 text-sm font-medium">Active</p>
              <p className="text-2xl font-bold text-green-700">
                {countsData?.getCountOfProducts?.find(c => c.productStatus === 'ACTIVE')?.count || 0}
              </p>
            </div>
            <div className="h-[92px] bg-yellow-50 border border-yellow-300 rounded-xl p-3">
              <p className="text-yellow-600 text-sm font-medium">Draft</p>
              <p className="text-2xl font-bold text-yellow-600">
                {countsData?.getCountOfProducts?.find(c => c.productStatus === 'DRAFT')?.count || 0}
              </p>
            </div>
            <div className="h-[92px] bg-red-50 border border-red-300 rounded-xl p-3">
              <p className="text-red-600 text-sm font-medium">Expired</p>
              <p className="text-2xl font-bold text-red-600">
                {countsData?.getCountOfProducts?.find(c => c.productStatus === 'EXPIRED')?.count || 0}
              </p>
            </div>
          </div>
        )}

        {/* Filters + Upload Button */}
        <div className="mb-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <div className="flex gap-3 flex-wrap">
              {['all', 'active', 'draft', 'expired'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors capitalize ${filterStatus === status
                    ? status === 'all' || status === 'expired'
                      ? 'bg-[#AD0101] text-white'
                      : status === 'active' ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'
                    : 'bg-[#E0E0E0] text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {status === 'all' ? 'All Updates' : status}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setShowForm(true); setErrors({}); }}
              className="flex items-center justify-center h-10 gap-2 bg-[#AD0101] text-white px-6 rounded-md text-sm font-medium hover:bg-red-700"
            >
              Upload New Update
            </button>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 space-y-3">
                <h2 className="text-xl font-semibold text-gray-900">
                  Primary Details
                </h2>
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-[#AD0101]">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={`w-full h-10 px-3 text-sm border ${errors.title ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                    placeholder="Enter title"
                  />
                  {errors.title && <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1"><AlertCircle size={12} /> {errors.title}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-[#AD0101]">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className={`w-full h-28 px-3 py-2 text-sm border ${errors.description ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg resize-none`}
                    placeholder="Enter description"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                  />
                  {errors.description && <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1"><AlertCircle size={12} /> {errors.description}</p>}
                </div>

                {/* Product Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Name <span className="text-[#AD0101]">*</span>
                  </label>
                  <input
                    type="text"
                    name="productName"
                    value={formData.productName}
                    onChange={handleInputChange}
                    className={`w-full h-10 px-3 text-sm border ${errors.productName ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                    placeholder="Enter product name"
                  />
                  {errors.productName && <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1"><AlertCircle size={12} /> {errors.productName}</p>}
                </div>

                {/* Content Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content Type <span className="text-[#AD0101]">*</span>
                  </label>
                  <div className="flex flex-wrap items-center gap-x-7 gap-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contentType"
                        value="text"
                        checked={formData.contentType === 'text'}
                        onChange={handleInputChange}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSubmit(e);
                          }
                        }}
                        className="w-4 h-4 text-[#AD0101] accent-[#AD0101] focus:ring-[#AD0101] focus:ring-offset-2 cursor-pointer"
                      />
                      <FileText size={18} className="text-gray-600" />
                      <span className="text-sm">Text Content</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="contentType"
                        value="video"
                        checked={formData.contentType === 'video'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#AD0101] accent-[#AD0101] focus:ring-[#AD0101] focus:ring-offset-2 cursor-pointer"
                      />
                      <Video size={18} className="text-gray-600" />
                      <span className="text-sm">Video Content</span>
                    </label>
                  </div>
                </div>

                {/* Text Content */}
                {formData.contentType === 'text' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Content <span className="text-[#AD0101]">*</span>
                    </label>
                    <textarea
                      name="content"
                      value={formData.content}
                      onChange={handleInputChange}
                      rows="4"
                      className={`w-full h-28 px-3 py-2 text-sm border ${errors.content ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg resize-none`}
                      placeholder="Enter text content"
                    />
                    {errors.content && <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1"><AlertCircle size={12} /> {errors.content}</p>}
                  </div>
                )}

                {/* Video Fields */}
                {formData.contentType === 'video' && (
                  <div className="space-y-3 p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">YouTube Code <span className="text-[#AD0101]">*</span></label>
                        <input
                          type="text"
                          name="youtubeCode"
                          value={formData.youtubeCode}
                          onChange={handleInputChange}
                          placeholder="Enter YouTube video code"
                          maxLength={11}
                          className={`w-full h-10 border ${errors.youtubeCode ? 'border-[#AD0101]' : 'border-gray-300'} rounded-md px-3 text-sm`}
                        />
                        {errors.youtubeCode && <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1"><AlertCircle size={12} /> {errors.youtubeCode}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Video Title <span className="text-[#AD0101]">*</span></label>
                        <input
                          type="text"
                          name="videoTitle"
                          value={formData.videoTitle}
                          onChange={handleInputChange}
                          placeholder="Enter title"
                          className={`w-full h-10 border ${errors.videoTitle ? 'border-[#AD0101]' : 'border-gray-300'} rounded-md px-3 text-sm`}
                        />
                        {errors.videoTitle && <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1"><AlertCircle size={12} /> {errors.videoTitle}</p>}
                      </div>



                      <div>
                        <label className="block text-sm font-medium mb-1">Thumbnail URL</label>
                        <input
                          type="text"
                          value={formData.thumbnailCode || ''}
                          readOnly
                          className="w-full h-10 border border-gray-300 rounded-md px-3 bg-gray-100 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Duration <span className="text-[#AD0101]">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.videoDuration}
                          onChange={handleDurationChange}
                          onFocus={(e) => e.target.select()}
                          placeholder="MM:SS"
                          className={`w-full h-10 border ${errors.videoDuration ? 'border-[#AD0101]' : 'border-gray-300'} rounded-md px-3 text-sm`}
                        />
                        {errors.videoDuration && <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1"><AlertCircle size={12} /> {errors.videoDuration}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Language <span className="text-[#AD0101]">*</span></label>
                        <select
                          name="language"
                          value={formData.language}
                          onChange={handleInputChange}
                          className={`w-full h-10 border ${errors.language ? 'border-[#AD0101]' : 'border-gray-300'} rounded-md px-3 text-sm`}
                        >
                          <option value="">Select</option>
                          {languagesData?.mdLanguages?.map(lang => (
                            <option key={lang.id} value={lang.id}>{lang.name}</option>
                          ))}
                        </select>
                        {errors.language && <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1"><AlertCircle size={12} /> {errors.language}</p>}
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-1">
                          Description <span className="text-[#AD0101]">*</span>
                        </label>
                        <textarea
                          name="videoDescription"
                          value={formData.videoDescription}
                          onChange={handleInputChange}
                          rows="3"
                          placeholder="Detailed description of the video..."
                          className={`w-full h-28 px-3 py-2 text-sm border ${errors.videoDescription ? 'border-[#AD0101]' : 'border-gray-300'} rounded-md resize-none`}
                        />
                        {errors.videoDescription && (
                          <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1">
                            <AlertCircle size={12} /> {errors.videoDescription}
                          </p>
                        )}
                      </div>
                    </div>

                    {formData.thumbnailCode && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">Thumbnail Preview</p>
                        <img
                          src={formData.thumbnailCode}
                          alt="Video thumbnail"
                          className="w-full max-w-md mx-auto h-28 object-cover rounded-lg border shadow-md"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/480x360?text=Invalid+Code'}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Publish Date <span className="text-[#AD0101]">*</span>
                    </label>
                    <input
                      type="date"
                      name="publishDate"
                      value={formData.publishDate}
                      onChange={handleInputChange}
                      className={`w-full h-10 px-3 text-sm border ${errors.publishDate ? 'border-[#AD0101]' : 'border-gray-300'} rounded-lg`}
                    />
                    {errors.publishDate && <p className="mt-1 text-xs text-[#AD0101] flex items-center gap-1"><AlertCircle size={12} /> {errors.publishDate}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (Optional)</label>
                    <input
                      type="date"
                      name="expiryDate"
                      value={formData.expiryDate}
                      onChange={handleInputChange}
                      className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex-1 h-9 bg-[#AD0101] text-white text-sm rounded-md font-medium hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : (editingId ? 'Update' : 'Publish')}
                  </button>
                  <button
                    onClick={resetForm}
                    className="h-9 px-5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 size={20} className="text-red-600" />
                </div>
                <h3 className="text-lg font-semibold">Delete Update?</h3>
              </div>
              <p className="text-gray-600 mb-6">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={confirmDelete} disabled={deleteLoading} className="flex-1 bg-[#AD0101] text-white py-2 rounded-lg hover:bg-[#AD0101] disabled:opacity-70 disabled:cursor-not-allowed">
                  {deleteLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Delete'}
                </button>
                <button onClick={() => setShowDeleteModal(false)} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Updates List */}
        <div className="space-y-3">
          {filteredUpdates.length === 0 ? (
            <div className="min-h-[180px]" aria-live="polite"></div>
          ) : (
            filteredUpdates.map(update => {
              const status = update.productStatus || 'DRAFT';
              const statusColors = {
                ACTIVE: 'bg-green-100 text-green-800 border-green-200',
                DRAFT: 'bg-yellow-100 text-yellow-800 border-yellow-200',
                EXPIRED: 'bg-red-100 text-red-800 border-red-200'
              };
              const badgeColor = statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';

              const formatDate = (dateStr) => {
                if (!dateStr) return '—';
                return new Date(dateStr).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                }).replace(/ /g, ' ');
              };

              return (
                <div key={update.id} className="bg-white rounded-lg shadow-sm border border-gray-300 p-3 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900">{update.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${badgeColor}`}>
                          {status}
                        </span>
                        {update.productVideo ? <Video size={15} className="text-purple-600" /> : <FileText size={15} className="text-gray-700" />}
                      </div>
                      <p className="text-gray-600 text-xs mb-1">{update.shortDescription || update.description || 'No description'}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                        <span><strong>Product:</strong> {update.productName}</span>
                        <span className="flex items-center gap-1">
                          <Calendar size={14} /> <strong>Publish:</strong> {formatDate(update.publishDate)}
                        </span>
                        {/* {update.expiryDate && ( */}
                        <span className="flex items-center gap-1">
                          <Calendar size={14} /> <strong>Expires:</strong> {formatDate(update.expiryDate)}
                        </span>
                        {/* )} */}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleEdit(update)} className="p-1 text-gray-600 hover:bg-gray-50 rounded" title="Edit">
                        <img src={editIcon} alt="" className="w-4 h-4 object-contain" />
                      </button>
                      <button onClick={() => openDeleteModal(update.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
    {filteredUpdates.length > 0 && (
      <div className="mt-5 flex justify-end items-center gap-4 pr-4">
        <button
          type="button"
          aria-label="Previous page"
          onClick={() => handlePageChange(pagination.current - 1)}
          disabled={loading || pagination.current <= 1}
          className="p-1 text-gray-500 disabled:text-gray-300 disabled:cursor-default"
        >
          <ChevronLeft size={22} strokeWidth={3} />
        </button>
        <span className="flex h-8 min-w-8 items-center justify-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-[#AD0101] shadow-sm">
          {pagination.current}
        </span>
        <button
          type="button"
          aria-label="Next page"
          onClick={() => handlePageChange(pagination.current + 1)}
          disabled={loading || pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
          className="p-1 text-gray-500 disabled:text-gray-300 disabled:cursor-default"
        >
          <ChevronRight size={22} strokeWidth={3} />
        </button>
      </div>
    )}
    </>
  );
};

export default ProductUpdatesAdmin;